import React from "react";
import FeedResource from "client/resources/feedResource";
import useRequiredContext from "hooks/useRequiredContext";
import { NameOrIdKey } from "components/KeyAccessProvider/types";
import { useKeyedItemAccess } from "components/KeyAccessProvider/KeyedItemAccessProvider";

type ProcessFeedsContextValue = FeedResource[];
type ProcessFeedsRefreshContextValue = () => Promise<unknown>;

const ProcessFeedsContext = React.createContext<ProcessFeedsContextValue>([]);
const RefreshProcessFedsContext = React.createContext<ProcessFeedsRefreshContextValue | undefined>(undefined);

export function useFeedsFromContext() {
    return useRequiredContext(ProcessFeedsContext, "ProcessFeedsContext");
}

export function useRefreshFeedsFromContext() {
    return useRequiredContext(RefreshProcessFedsContext, "RefreshProcessFeedsContext");
}

export function useFeedFromContext(nameOrId: string, lookupKeyOverride?: NameOrIdKey) {
    const contextKey = useKeyedItemAccess();
    const feeds = useFeedsFromContext();
    const lookupKey = lookupKeyOverride ?? contextKey;

    return React.useMemo(() => {
        return feeds.find((f) => f[lookupKey] === nameOrId);
    }, [feeds, lookupKey, nameOrId]);
}

type ProcessFeedsContextProviderProps = {
    feeds: FeedResource[];
    refreshFeeds: () => Promise<unknown>;
};

type ProcessFeedConsumerProps = {
    feedNameOrId: string;
    children: (feed: FeedResource | undefined) => React.ReactElement;
};

export function ProcessFeedLookup(props: ProcessFeedConsumerProps) {
    const feed = useFeedFromContext(props.feedNameOrId);
    return props.children(feed);
}

export function ProcessFeedsContextProvider(props: React.PropsWithChildren<ProcessFeedsContextProviderProps>) {
    return (
        <ProcessFeedsContext.Provider value={props.feeds}>
            <RefreshProcessFedsContext.Provider value={props.refreshFeeds}>{props.children}</RefreshProcessFedsContext.Provider>
        </ProcessFeedsContext.Provider>
    );
}
